import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_REQUEST_DATE_FORMAT, NUMBER_MONTHS_IN_YEAR } from 'constants/date';
import { OccupancyType, PropertyType } from 'constants/loanApplication';
import { getDateValueInForm } from 'utils/loanManagement';
import { extractPendingChangesFromJson } from 'utils/pendingChanges';

import {
  omittedPropertyExpensesFieldsToSend,
  proposedPropertyExpensesFieldsMap,
} from './PropertyExpenses.constants';

const getDueDateInForm = dueDate =>
  dayjs.isDayjs(dueDate) ? dueDate : getDateValueInForm(dueDate);

// The API stores due dates as UTC midnight; the date picker renders a raw UTC string in local time,
// which shows the previous calendar day in any timezone behind UTC.
export const getPropertyTaxFormData = data => {
  if (!data.amountDueDates) {
    return data;
  }

  return {
    ...data,
    amountDueDates: data.amountDueDates.map(amountDueDate => ({
      ...amountDueDate,
      dueDate: getDueDateInForm(amountDueDate.dueDate),
    })),
  };
};

const keepUntouchedDueDates = (updatedAmountDueDates, initialAmountDueDates = []) =>
  updatedAmountDueDates.map((amountDueDate, index) => {
    const initialDueDate = initialAmountDueDates[index]?.dueDate;
    const isDueDateUntouched =
      initialDueDate &&
      dayjs.isDayjs(amountDueDate.dueDate) &&
      getDueDateInForm(initialDueDate).isSame(amountDueDate.dueDate, 'day');

    return isDueDateUntouched ? { ...amountDueDate, dueDate: initialDueDate } : amountDueDate;
  });

export const getValuesToSave = ({ updatedData, initialData }) => {
  const { impoundTypeId, frequency, amountDueDates } = updatedData;

  const normalizedUpdatedData = {
    ...updatedData,
    ...(impoundTypeId && { impoundTypeId: impoundTypeId.value }),
    ...(frequency && { frequency: frequency.value }),
    ...(amountDueDates && {
      amountDueDates: keepUntouchedDueDates(amountDueDates, initialData.amountDueDates),
    }),
  };

  const formattedUpdatedData = {
    ...initialData,
    ...normalizedUpdatedData,
  };
  const omittedData = _.omit(formattedUpdatedData, ['isNewlyAdded']);

  return omittedData;
};

export const createNewTableData = () => {
  return {
    id: _.uniqueId(),
    isNewlyAdded: true,
    isAdded: true,
  };
};

// A picked date is a local-midnight dayjs; serializing it as a UTC timestamp shifts the calendar day,
// so send date-only like the homeowners insurance policy due date. Stored strings go back untouched.
const formatDueDateToSend = dueDate =>
  dayjs.isDayjs(dueDate) ? dueDate.format(COMMON_REQUEST_DATE_FORMAT) : dueDate;

export const prepareNestedProposedPropertyExpensesToSend = array => {
  return array?.reduce(
    (acc, item) => {
      const tax = {
        ...item,
        annualAmount: item.monthlyAmount * NUMBER_MONTHS_IN_YEAR,
        overrodeAnnualAmount: null,
        ...(item.amountDueDates && {
          amountDueDates: item.amountDueDates.map(amountDueDate => ({
            ...amountDueDate,
            dueDate: formatDueDateToSend(amountDueDate.dueDate),
          })),
        }),
      };

      if (tax.isAdded) {
        acc.create.push(tax);
      }

      if (tax.isDeleted) {
        acc.delete.push({ applicationImpoundId: tax.applicationImpoundId });
      }

      if (tax.isEdited) {
        acc.update[tax.applicationImpoundId] = _.omit(tax, omittedPropertyExpensesFieldsToSend);
      }

      return acc;
    },
    { create: [], delete: [], update: {} },
  );
};

export const mapProposedPropertyExpensesFields = data => {
  const mapped = {};

  Object.entries(data).forEach(([key, value]) => {
    const newKey = proposedPropertyExpensesFieldsMap[key];

    if (newKey) {
      mapped[newKey] = value;
    }

    if (!newKey) {
      mapped[key] = value;
    }
  });

  return mapped;
};

const getIsOverrodeMonthlyAmountRendered = ({
  taxesInsuranceEscrowed,
  initialOverrodeMonthlyAmount,
  qualifyingForMonthlyAmount,
}) => (taxesInsuranceEscrowed ? qualifyingForMonthlyAmount : !!initialOverrodeMonthlyAmount);

export const getMonthlyPropertyTaxes = ({ propertyTaxes, taxesInsuranceEscrowed }) => {
  if (!propertyTaxes?.length) {
    return 0;
  }

  return propertyTaxes.reduce(
    (acc, { overrodeMonthlyAmount, monthlyAmount, propertyTaxQualifyingForMonthlyAmount }) => {
      const isOverrodeMonthlyAmountRendered = getIsOverrodeMonthlyAmountRendered({
        taxesInsuranceEscrowed,
        qualifyingForMonthlyAmount: propertyTaxQualifyingForMonthlyAmount,
        initialOverrodeMonthlyAmount: overrodeMonthlyAmount,
      });

      return (
        acc + Number(isOverrodeMonthlyAmountRendered ? overrodeMonthlyAmount : monthlyAmount) || 0
      );
    },
    0,
  );
};

export const enhancePropertyExpensesPendingChanges = (data, isAnnualAmount = true) => {
  const result = {};

  Object.entries(data).forEach(([key, { annualAmount, impoundAmountDueDates, ...entry }]) => {
    if (!annualAmount) {
      result[key] = entry;

      return;
    }

    result[key] = {
      ...entry,
      ...(isAnnualAmount && { annualAmount }),
      monthlyAmount: {
        newValue: `${annualAmount.newValue / NUMBER_MONTHS_IN_YEAR}`,
        initialValue: `${annualAmount.initialValue / NUMBER_MONTHS_IN_YEAR}`,
        actionType: annualAmount.actionType,
      },
      ...(impoundAmountDueDates && {
        amountDueDates: extractPendingChangesFromJson(impoundAmountDueDates),
      }),
    };
  });

  return result;
};

const checkIsInvestmentProperty = occupancy => occupancy === OccupancyType.INVESTMENT_PROPERTY;

export const checkIsAmountOfRentCollectedActive = ({ occupancy, propertyType }) => {
  const isInvestmentProperty = checkIsInvestmentProperty(occupancy);
  const isPrimaryMultiUnitProperty = propertyType === PropertyType.MULTI_UNIT;

  return isPrimaryMultiUnitProperty || isInvestmentProperty;
};

export const checkIsAmountOfRentCollectedActivePendingChange = ({ pendingChanges }) =>
  !(
    [undefined, OccupancyType.INVESTMENT_PROPERTY].includes(pendingChanges?.occupancy?.newValue) ||
    [undefined, PropertyType.MULTI_UNIT].includes(pendingChanges?.propertyType?.newValue)
  );
